/**
 * Auto reconciliation modal body: displays correction details by type.
 */
import React from 'react';
import { Box, Flex, Icon, Text, VStack } from '@chakra-ui/react';
import { TbArrowRight, TbArrowDown } from 'react-icons/tb';
import { LineCard } from './LineCard';
import { getNexLineDisplayQty, type NexLineQuantityBasis } from '../../utils/nexLineQuantity';

const ACCENT = '#0071E3';

export interface ReconciliationAutoContentProps {
    correction: any;
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet';
    nexLineQuantityBasis?: NexLineQuantityBasis;
}

export function ReconciliationAutoContent({
    correction,
    invoiceType,
    nexLineQuantityBasis = 'qtyRec',
}: ReconciliationAutoContentProps) {
    if (!correction) return <Text color="gray.500">No details available.</Text>;

    const renderLineCard = (line: any) => (
        <LineCard line={line} prefixDollar invoiceType={invoiceType} nexLineQuantityBasis={nexLineQuantityBasis} />
    );

    if (correction.type === 'part_number_auto_corrected') {
        return (
            <VStack spacing={4} align="stretch">
                <Text fontSize="md" color="gray.600">The part number was automatically corrected to match the NEX purchase order.</Text>
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <Flex align="center" gap={4}>
                        <Box flex={1} bg="white" borderRadius="lg" p={5} border="1px solid" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={3}>Original</Text>
                            <Text fontSize="xl" fontWeight="bold" color="gray.900" wordBreak="break-word" fontFamily="mono">{correction.originalPartNumber ?? '—'}</Text>
                        </Box>
                        <Icon as={TbArrowRight as React.ElementType} boxSize={6} color={ACCENT} flexShrink={0} />
                        <Box flex={1} bg="white" borderRadius="lg" p={5} border="1px solid" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={3}>Current</Text>
                            <Text fontSize="xl" fontWeight="bold" color="gray.900" wordBreak="break-word" fontFamily="mono">{correction.partNumber}</Text>
                        </Box>
                    </Flex>
                </Box>
            </VStack>
        );
    }

    if (correction.type === 'supersession_auto_corrected') {
        const invoicePn = correction.originalPartNumber ?? correction.partNumber ?? '—';
        const nexPn =
            correction.matchedNexPartNumber ??
            (correction.originalPartNumber != null &&
            correction.partNumber != null &&
            correction.partNumber !== correction.originalPartNumber
                ? correction.partNumber
                : '—');
        return (
            <VStack spacing={4} align="stretch">
                <Text fontSize="md" color="gray.600">
                    The invoice keeps the part number from the document. Motor reports a supersession; the line is matched to the current part on the NEX purchase order.
                </Text>
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <Flex align="center" gap={4}>
                        <Box flex={1} bg="white" borderRadius="lg" p={5} border="1px solid" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={3}>Invoice (superseded part)</Text>
                            <Text fontSize="xl" fontWeight="bold" color="gray.900" wordBreak="break-word" fontFamily="mono">{invoicePn}</Text>
                        </Box>
                        <Icon as={TbArrowRight as React.ElementType} boxSize={6} color={ACCENT} flexShrink={0} />
                        <Box flex={1} bg="white" borderRadius="lg" p={5} border="1px solid" borderColor="gray.200">
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={3}>Matched NEX (current)</Text>
                            <Text fontSize="xl" fontWeight="bold" color="gray.900" wordBreak="break-word" fontFamily="mono">{nexPn}</Text>
                        </Box>
                    </Flex>
                </Box>
            </VStack>
        );
    }

    if (correction.type === 'expanded_to_match_nex') {
        const origLine = correction.originalLine;
        const newLines = correction.newLines ?? [];
        return (
            <VStack spacing={4} align="stretch">
                <Text fontSize="md" color="gray.600">This line item was split into {newLines.length} separate lines to match the NEX purchase order.</Text>
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Original (1 line)</Text>
                            {origLine ? renderLineCard(origLine) : <Text fontSize="sm" color="gray.400">Original data not available</Text>}
                        </Box>
                        <Flex justify="center"><Icon as={TbArrowDown as React.ElementType} boxSize={6} color={ACCENT} /></Flex>
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Current ({newLines.length} lines)</Text>
                            <VStack spacing={2} align="stretch">
                                {newLines.map((line: any, li: number) => (
                                    <Box key={li}>{renderLineCard(line)}</Box>
                                ))}
                            </VStack>
                        </Box>
                    </VStack>
                </Box>
            </VStack>
        );
    }

    if (correction.type === 'consolidated_to_match_nex') {
        const origLines = correction.originalLines ?? [];
        const newLine = correction.newLine ?? correction.newLines?.[0];
        const combinedCount = origLines.length;
        const summaryText = combinedCount >= 2
            ? `${combinedCount} lines for part ${correction.partNumber} were combined into one line to match the NEX purchase order.`
            : `Part ${correction.partNumber} was adjusted to match the NEX purchase order structure.`;
        return (
            <VStack spacing={4} align="stretch">
                <Text fontSize="md" color="gray.600">{summaryText}</Text>
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Original{combinedCount >= 2 ? ` (${combinedCount} lines)` : ''}</Text>
                            <VStack spacing={2} align="stretch">
                                {origLines.map((line: any, li: number) => (
                                    <Box key={li}>{renderLineCard(line)}</Box>
                                ))}
                            </VStack>
                        </Box>
                        <Flex justify="center"><Icon as={TbArrowDown as React.ElementType} boxSize={6} color={ACCENT} /></Flex>
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Current (1 line)</Text>
                            {newLine ? renderLineCard(newLine) : <Text fontSize="sm" color="gray.400">Current data not available</Text>}
                        </Box>
                    </VStack>
                </Box>
            </VStack>
        );
    }

    if (correction.type === 'probable_match') {
        const nexLine = correction.nexLine;
        const nexPartNumber = nexLine?.partNumber || '—';
        const nexDescription = nexLine?.partDesc || nexLine?.description || '—';
        const nexQty = getNexLineDisplayQty(nexLine, invoiceType, nexLineQuantityBasis);
        const nexUnitPrice = nexLine?.unitCost || nexLine?.unit_price || 0;
        const nexExtended = nexLine?.extendedCost || nexLine?.extended_cost || 0;
        const nexListPrice = nexLine?.listPrice ?? nexLine?.list_price ?? null;
        return (
            <VStack spacing={4} align="stretch">
                <Text fontSize="md" color="gray.600">This line item was matched to a NEX purchase order entry by description and price, even though the part numbers differ.</Text>
                <Box bg="gray.50" borderRadius="xl" p={6}>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Invoice Line Item</Text>
                            {renderLineCard({
                                title: correction.title,
                                partNumber: correction.partNumber,
                                qty: correction.qty,
                                listPrice: correction.listPrice,
                                unitBuyPrice: correction.unitBuyPrice,
                                totalBuyPrice: correction.totalBuyPrice,
                            })}
                        </Box>
                        <Flex justify="center"><Icon as={TbArrowDown as React.ElementType} boxSize={6} color={ACCENT} /></Flex>
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="blue.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Matched NEX Line Item</Text>
                            {renderLineCard({
                                title: nexDescription,
                                partNumber: nexPartNumber,
                                qty: nexQty,
                                listPrice: nexListPrice,
                                unitBuyPrice: nexUnitPrice,
                                totalBuyPrice: nexExtended,
                            })}
                        </Box>
                    </VStack>
                </Box>
            </VStack>
        );
    }

    const origLines = correction.originalLines ?? (correction.originalLine ? [correction.originalLine] : []);
    const newLines = correction.newLines ?? (correction.newLine ? [correction.newLine] : []);
    return (
        <VStack spacing={4} align="stretch">
            <Text fontSize="md" color="gray.600">Part {correction.partNumber} was normalized to match the NEX purchase order.</Text>
            <Box bg="gray.50" borderRadius="xl" p={6}>
                <VStack spacing={4} align="stretch">
                    {origLines.length > 0 && (
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Original</Text>
                            <VStack spacing={2} align="stretch">
                                {origLines.map((line: any, li: number) => (
                                    <Box key={li}>{renderLineCard(line)}</Box>
                                ))}
                            </VStack>
                        </Box>
                    )}
                    {origLines.length > 0 && newLines.length > 0 && (
                        <Flex justify="center"><Icon as={TbArrowDown as React.ElementType} boxSize={6} color={ACCENT} /></Flex>
                    )}
                    {newLines.length > 0 && (
                        <Box>
                            <Text fontSize="xs" fontWeight="bold" color="orange.500" textTransform="uppercase" letterSpacing="wider" mb={2}>Current</Text>
                            <VStack spacing={2} align="stretch">
                                {newLines.map((line: any, li: number) => (
                                    <Box key={li}>{renderLineCard(line)}</Box>
                                ))}
                            </VStack>
                        </Box>
                    )}
                </VStack>
            </Box>
        </VStack>
    );
}
